import { useCallback, useMemo } from "react";
import { createPairId, normalizeCpf } from "../../challenges/utils/challengePairs";
import { pickPreferredRegistration } from "../../events/shared/utils/eventRegistrationFormatters";
import type { AdminEventRegistration } from "../services/eventRegistrationAdminService";
import type { TournamentRegistrationCategoryItem } from "../utils/tournamentCategories";

type UseAdminChallengeRegistrationDerivedInput = {
  activeRegistrationCategoryKeyForModal: string;
  challengeEventRegistrations: AdminEventRegistration[];
  challengeTournamentCategories: TournamentRegistrationCategoryItem[];
  countFixedPairAsPairs?: boolean;
  isTournamentChallenge: boolean;
};

export function useAdminChallengeRegistrationDerived({
  activeRegistrationCategoryKeyForModal,
  challengeEventRegistrations,
  challengeTournamentCategories,
  countFixedPairAsPairs = false,
  isTournamentChallenge,
}: UseAdminChallengeRegistrationDerivedInput) {
  const tournamentParticipantsSummaryByCategory = useMemo(() => {
    if (!isTournamentChallenge) {
      return {} as Record<
        string,
        {
          current: number;
          limit: number;
          registrationType: "individual" | "fixed_pair";
        }
      >;
    }
    const summary: Record<
      string,
      {
        current: number;
        limit: number;
        registrationType: "individual" | "fixed_pair";
      }
    > = {};
    challengeTournamentCategories.forEach((category) => {
      const key = String(category.categoryKey || "").trim();
      if (!key) return;
      const registrationType =
        category.registrationType === "fixed_pair"
          ? "fixed_pair"
          : "individual";
      const slotLimit = Math.max(
        0,
        Math.floor(Number(category.slotLimit || 0)),
      );
      summary[key] = {
        current: 0,
        limit:
          registrationType === "fixed_pair" && !countFixedPairAsPairs
            ? slotLimit * 2
            : slotLimit,
        registrationType,
      };
    });

    const preferredByParticipantKey = new Map<string, AdminEventRegistration>();
    challengeEventRegistrations.forEach((row) => {
      if (row.status === "canceled") return;
      const rawKey = String(row.registrationCategoryKey || "").trim();
      const categoryKey =
        rawKey ||
        (challengeTournamentCategories.length === 1
          ? String(challengeTournamentCategories[0]?.categoryKey || "").trim()
          : "");
      if (!categoryKey || !summary[categoryKey]) return;
      if (row.registrationType === "fixed_pair") {
        const cpf1 = normalizeCpf(row.userCpf);
        const cpf2 = normalizeCpf(row.partnerCpf);
        if (cpf1.length < 8 || cpf2.length < 8 || cpf1 === cpf2) return;
        const pairId = createPairId(cpf1, cpf2);
        const participantKey = `pair:${categoryKey}:${pairId}`;
        preferredByParticipantKey.set(
          participantKey,
          pickPreferredRegistration(
            preferredByParticipantKey.get(participantKey),
            row,
          ),
        );
        return;
      }
      const cpf = normalizeCpf(row.userCpf);
      if (cpf.length < 8) return;
      const participantKey = `player:${categoryKey}:${cpf}`;
      preferredByParticipantKey.set(
        participantKey,
        pickPreferredRegistration(
          preferredByParticipantKey.get(participantKey),
          row,
        ),
      );
    });

    preferredByParticipantKey.forEach((row, participantKey) => {
      const [, categoryKey] = participantKey.split(":", 3);
      if (!categoryKey || !summary[categoryKey]) return;
      summary[categoryKey].current +=
        row.registrationType === "fixed_pair" && !countFixedPairAsPairs ? 2 : 1;
    });

    return summary;
  }, [
    challengeEventRegistrations,
    challengeTournamentCategories,
    countFixedPairAsPairs,
    isTournamentChallenge,
  ]);

  const getTournamentPairCapacityForCategory = useCallback(
    (
      categoryKey: string,
      editingRegistration?: AdminEventRegistration | null,
    ) => {
      const key = String(categoryKey || "").trim();
      const summary = tournamentParticipantsSummaryByCategory[key];
      const limit = Math.max(0, Math.floor(Number(summary?.limit || 0)));
      const current = Math.max(0, Math.floor(Number(summary?.current || 0)));
      const registrationCategoryKey = String(
        editingRegistration?.registrationCategoryKey || "",
      ).trim();
      const shouldDiscountEditingRegistration =
        Boolean(editingRegistration) &&
        editingRegistration?.status !== "canceled" &&
        registrationCategoryKey === key;
      const editingRegistrationSize = shouldDiscountEditingRegistration
        ? editingRegistration?.registrationType === "fixed_pair" &&
          !countFixedPairAsPairs
          ? 2
          : 1
        : 0;
      const currentWithoutEditing = Math.max(
        0,
        current - editingRegistrationSize,
      );
      const nextRegistrationSize =
        summary?.registrationType === "fixed_pair" && countFixedPairAsPairs
          ? 1
          : 2;
      const next = currentWithoutEditing + nextRegistrationSize;
      return {
        current,
        currentWithoutEditing,
        limit,
        next,
        exceeds: limit > 0 && next > limit,
        remaining: limit > 0 ? Math.max(0, limit - currentWithoutEditing) : 0,
      };
    },
    [countFixedPairAsPairs, tournamentParticipantsSummaryByCategory],
  );

  const activeTournamentCategoryPairCapacity = useMemo(
    () =>
      getTournamentPairCapacityForCategory(
        activeRegistrationCategoryKeyForModal,
      ),
    [
      activeRegistrationCategoryKeyForModal,
      getTournamentPairCapacityForCategory,
    ],
  );

  const challengeFixedPairRegistrations = useMemo(
    () =>
      challengeEventRegistrations.filter((row) => {
        if (row.registrationType !== "fixed_pair") return false;
        if (row.status === "canceled") return false;
        if (
          isTournamentChallenge &&
          String(activeRegistrationCategoryKeyForModal || "").trim()
        ) {
          if (
            String(row.registrationCategoryKey || "").trim() !==
            String(activeRegistrationCategoryKeyForModal || "").trim()
          ) {
            return false;
          }
        }
        const cpf1 = normalizeCpf(row.userCpf);
        const cpf2 = normalizeCpf(row.partnerCpf);
        return cpf1.length >= 8 && cpf2.length >= 8 && cpf1 !== cpf2;
      }),
    [
      activeRegistrationCategoryKeyForModal,
      challengeEventRegistrations,
      isTournamentChallenge,
    ],
  );

  const challengeRegistrationByPairId = useMemo(() => {
    const map: Record<string, AdminEventRegistration> = {};
    challengeFixedPairRegistrations.forEach((row) => {
      const cpf1 = normalizeCpf(row.userCpf);
      const cpf2 = normalizeCpf(row.partnerCpf);
      if (cpf1.length < 8 || cpf2.length < 8 || cpf1 === cpf2) return;
      const pairId = createPairId(cpf1, cpf2);
      map[pairId] = pickPreferredRegistration(map[pairId], row);
    });
    return map;
  }, [challengeFixedPairRegistrations]);

  const challengeIndividualRegistrations = useMemo(
    () =>
      challengeEventRegistrations.filter((row) => {
        if (row.registrationType === "fixed_pair") return false;
        if (row.status === "canceled") return false;
        if (
          isTournamentChallenge &&
          String(activeRegistrationCategoryKeyForModal || "").trim()
        ) {
          if (
            String(row.registrationCategoryKey || "").trim() !==
            String(activeRegistrationCategoryKeyForModal || "").trim()
          ) {
            return false;
          }
        }
        const cpf = normalizeCpf(row.userCpf);
        return cpf.length >= 8;
      }),
    [
      activeRegistrationCategoryKeyForModal,
      challengeEventRegistrations,
      isTournamentChallenge,
    ],
  );

  const challengeRegistrationByCpf = useMemo(() => {
    const map: Record<string, AdminEventRegistration> = {};
    challengeIndividualRegistrations.forEach((row) => {
      const cpf = normalizeCpf(row.userCpf);
      if (cpf.length < 8) return;
      map[cpf] = pickPreferredRegistration(map[cpf], row);
    });
    return map;
  }, [challengeIndividualRegistrations]);

  return {
    activeTournamentCategoryPairCapacity,
    challengeFixedPairRegistrations,
    challengeIndividualRegistrations,
    challengeRegistrationByCpf,
    challengeRegistrationByPairId,
    getTournamentPairCapacityForCategory,
    tournamentParticipantsSummaryByCategory,
  };
}
